import {
  SelectOption,
  AttributeGroupDTO,
  AttributeGroupType,
  AttributeGroupValueDTO
} from '@touche-lts/panel-ui/dist'
import { makeAutoObservable } from 'mobx'
import RootStore  from 'mobx-stores/root-store'
// import {
//   AttributeGroupDTO,
//   AttributeGroupType,
//   AttributeGroupValueDTO
// } from 'models'
import { Api } from 'services'
import { generateLocalId, isLocalId } from 'utilities'
import { ColorPickerUtil } from './store-attributes-store.utils'

export class StoreAttributesStore {
  private rootStore:any
  private storeApi: Api['storeApi']

  constructor (rootStore:any) {
    this.rootStore = rootStore
    this.storeApi = rootStore.generalStore.api.storeApi
    makeAutoObservable(this)
  }

  private attributeGroupsBeforeChange: AttributeGroupDTO[] = null

  // OBSERVABLES
  public attributeGroups: AttributeGroupDTO[] = []
  public editMode: boolean = false

  // ACTIONS
  private setAttributeGroups = (attributeGroups: AttributeGroupDTO[]): void => {
    this.attributeGroups = attributeGroups
  }

  private setEditMode = (value: boolean): void => {
    this.editMode = value
  }

  // METHODS
  public clearStore = (): void => {
    this.setAttributeGroups([])
    this.setEditMode(false)
    this.attributeGroupsBeforeChange = null
  }

  public onStoreAttributesSectionMount = async (): Promise<void> => {
    if (!this.storeId) return
    const attributeGroups = await this.storeApi.getAttributeGroups(this.storeId)
    if (!attributeGroups) return
    this.setAttributeGroups(attributeGroups)
  }

  public onEditButtonClick = (): void => {
    this.attributeGroupsBeforeChange = this.attributeGroups
    this.setEditMode(true)
  }

  public onCancelEditClick = (): void => {
    this.setAttributeGroups(this.attributeGroupsBeforeChange || [])
    this.attributeGroupsBeforeChange = null
    this.setEditMode(false)
  }

  public onSaveClick = async (): Promise<void> => {
    const attributeGroupsUpdate = [
      ...this.attributeGroups.filter(({ name, type }) => Boolean(name && type))
    ]
    attributeGroupsUpdate.forEach(attributeGroup => {
      if (isLocalId(attributeGroup.id)) {
        attributeGroup.id = null
      }
      attributeGroup.values.forEach(value => {
        if (isLocalId(value.id)) {
          value.id = null
        }
      })
    })
    const attributeGroups = await this.storeApi.updateAttributeGroups(
      this.storeId,
      attributeGroupsUpdate
    )
    if (!attributeGroups) return

    this.setAttributeGroups(attributeGroups)
    this.setEditMode(false)
    this.attributeGroupsBeforeChange = null
  }

  public onRemoveAttributeClick = (attributeId: number): void => {
    this.setAttributeGroups(
      this.attributeGroups.filter(({ id }) => id !== attributeId)
    )
  }

  public setAttributeGroupProps = (
    attributeGroupId: number,
    props: Partial<AttributeGroupDTO>
  ): void => {
    const attributeGroupIndex = this.attributeGroups.findIndex(
      attrGroup => attrGroup.id === attributeGroupId
    )
    const attributeGroup = this.attributeGroups[attributeGroupIndex]
    if (!attributeGroup) return
    const attributeGroupUpdated = { ...attributeGroup, ...props }
    const attributeGroupsUpdated = [...this.attributeGroups]
    attributeGroupsUpdated[attributeGroupIndex] = attributeGroupUpdated
    this.setAttributeGroups(attributeGroupsUpdated)
  }

  private getAttributeGroupById = (id: number): AttributeGroupDTO => {
    return this.attributeGroups.find(attrGroup => attrGroup.id === id)
  }

  public addAttributeValue = (
    attributeGroupId: number,
    attributeValue: string
  ): void => {
    const newAttributeValue: AttributeGroupValueDTO = {
      id: generateLocalId(),
      createdDate: null,
      name: attributeValue,
      status: null,
      value: attributeValue,
      groupId: attributeGroupId
    }
    const attributeGroup = this.getAttributeGroupById(attributeGroupId)
    if (!attributeGroup) return

    const applyNewAttributeValue = () => {
      const attributeGroupValues = [...(attributeGroup.values || [])]
      attributeGroupValues.push(newAttributeValue)
      this.setAttributeGroupProps(attributeGroupId, {
        values: attributeGroupValues
      })
    }

    const isColor = attributeGroup.type === AttributeGroupType.COLOR

    if (isColor) {
      newAttributeValue.value = '#000000'
      const { openDialogPopup, closeDialogPopup } = this.rootStore.generalStore
      openDialogPopup({
        children: ColorPickerUtil({
          onChange: colorValue => {
            newAttributeValue.value = colorValue
          },
          // width: 590,
          initialValue: newAttributeValue.value
        }),
        preset: 'leftAligned',
        headingComponent: 'Numbers',
        heading: `Choose the value for "${attributeValue}" color`,
        closeButtonLabel: 'Cancel',
        buttons: [
          {
            preset: 'primary',
            children: 'Save',
            onClick: () => {
              applyNewAttributeValue()
              closeDialogPopup()
            }
          }
        ]
      })
    } else {
      applyNewAttributeValue()
    }
  }

  public removeAttributeValue = (
    attributeGroupId: number,
    attributeGroupValueId: number
  ): void => {
    const attributeGroup = this.getAttributeGroupById(attributeGroupId)
    if (!attributeGroup) return

    const attributeGroupValues = [...(attributeGroup.values || [])].filter(
      value => value.id !== attributeGroupValueId
    )
    this.setAttributeGroupProps(attributeGroupId, {
      values: attributeGroupValues
    })
  }

  public onAddAttributeGroupClick = (): void => {
    const newAttributeGroup: AttributeGroupDTO = {
      id: generateLocalId(),
      createdDate: null,
      name: '',
      opt: null,
      status: null,
      type: null,
      values: []
    }

    const currentAttributeGroups = [...this.attributeGroups]
    currentAttributeGroups.push(newAttributeGroup)

    this.setEditMode(true)
    if (!this.attributeGroupsBeforeChange) {
      this.attributeGroupsBeforeChange = this.attributeGroups
    }
    this.setAttributeGroups(currentAttributeGroups)
  }

  // COMPUTED
  private get storeId (): number {
    return this.rootStore.viewStorePageStore.storeId
  }

  public get haveAttributeGroups (): boolean {
    return Boolean(this.attributeGroups?.length)
  }

  public get attributeGroupTypeOptions (): SelectOption<AttributeGroupType>[] {
    return Object.values(AttributeGroupType).map(type => ({
      label: type,
      value: type
    }))
  }
}
